import { differenceInDays, format, getWeek } from 'date-fns';
import { PickerType } from '../../../global-state/types';
import { muiTheme } from '../../../theme';

const DATE_FORMATS = {
  date: 'dd-MMM-yyyy',
  month: 'MMM yyyy',
  range: 'dd-MMM-yyyy',
} as const;

const RANGE_CONFIG = {
  MIN_DAYS: 1,
  MAX_DAYS: 10,
  WINDOW_DAYS: 9,
} as const;

export const getRangeColors = (mode: 'light' | 'dark') => ({
  background: mode === 'dark' ? muiTheme.palette.primary.dark + '4A' : muiTheme.palette.primary.light + '4A',
  selected: muiTheme.palette.primary.main,
  selectedText: muiTheme.palette.primary.contrastText,
  hover: mode === 'dark' ? muiTheme.palette.primary.dark : muiTheme.palette.primary.light,
  invalid: muiTheme.palette.error.light + '1A',
});

export const orderDates = (date1: Date, date2: Date): [Date, Date] =>
  date1 < date2 ? [date1, date2] : [date2, date1];

export const isSameDate = (date1: Date | null, date2: Date | null): boolean => {
  if (!date1 || !date2) return false;
  return date1.toDateString() === date2.toDateString();
};

export const isRangeMode = (pickerType: PickerType): boolean => pickerType === 'range';

export const formatDateLabel = (date: Date, type: PickerType): string => {
  switch (type) {
    case 'week':
      return `Week ${getWeek(date)}`;
    case 'month':
      return format(date, DATE_FORMATS.month);
    case 'range':
      return 'Custom Range';
    default:
      return format(date, DATE_FORMATS.date);
  }
};

export const formatRangeLabel = (startDate: Date, endDate: Date): string =>
  `${format(startDate, DATE_FORMATS.range)} - ${format(endDate, DATE_FORMATS.range)}`;

export const validateRange = (start: Date, end: Date): boolean => {
  const days = Math.abs(differenceInDays(end, start)) + 1;
  return days >= RANGE_CONFIG.MIN_DAYS && days <= RANGE_CONFIG.MAX_DAYS;
};

export const isDateInWindow = (date: Date, rangeStart: Date): boolean => {
  const maxDate = new Date(rangeStart);
  maxDate.setDate(rangeStart.getDate() + RANGE_CONFIG.WINDOW_DAYS);
  const minDate = new Date(rangeStart);
  minDate.setDate(rangeStart.getDate() - RANGE_CONFIG.WINDOW_DAYS);
  return date >= minDate && date <= maxDate;
};

// style utils
export const createHoverHandlers = (setHovered: (date: Date | null) => void, day: Date) => ({
  onPointerEnter: () => setHovered(day),
  onPointerLeave: () => setHovered(null),
});

export const createRangeStyle = (isValid: boolean, rangeColors: ReturnType<typeof getRangeColors>) => ({
  backgroundColor: isValid ? rangeColors.selected : muiTheme.palette.error.main,
  '&:hover': {
    backgroundColor: isValid ? rangeColors.selected : muiTheme.palette.error.dark,
    opacity: 0.9,
  },
});
